import React, { useEffect, useState } from 'react';
import { productApi, categoryApi, uploadApi } from '../services/api';
import { Plus, Pencil, Trash2, Upload, Search, X, Package } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY = {
  name: '', description: '', price: '', stock: '', categoryId: '',
  subcategoryId: '', imageUrl: '', unit: 'kg', sku: '', tags: '', isActive: true,
  flavor: '', ingredients: '', directionsForUse: '', shelfLife: '',
  nutritionalFacts: [], recipeFor: [],
};

export default function AdminProducts() {
  const [products,      setProducts]      = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [modal,         setModal]         = useState(null); // null | { mode, data }
  const [uploading,     setUploading]     = useState(false);

  const load = async () => {
    setLoading(true);
    const [prodRes, catRes, subRes] = await Promise.all([
      productApi.getAll(),
      categoryApi.getAll(),
      categoryApi.getAllSubs(),
    ]);
    setProducts(prodRes.data);
    setCategories(catRes.data);
    setSubcategories(subRes.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.sku || '').toLowerCase().includes(search.toLowerCase())
  );

  const openAdd  = () => setModal({ mode: 'add',  data: { ...EMPTY } });
  const openEdit = (p) => setModal({
    mode: 'edit',
    data: {
      ...p,
      tags: (p.tags || []).join(', '),
      nutritionalFacts: p.nutritionalFacts || [],
      recipeFor: p.recipeFor || [],
    },
  });
  const closeModal = () => setModal(null);

  const updateField = (field, value) =>
    setModal((m) => ({ ...m, data: { ...m.data, [field]: value } }));

  // --- Repeatable-row helpers (nutritionalFacts: {label,value} / recipeFor: {ingredient,quantity}) ---
  const addRow = (field, emptyRow) =>
    setModal((m) => ({ ...m, data: { ...m.data, [field]: [...(m.data[field] || []), emptyRow] } }));

  const updateRow = (field, index, key, value) =>
    setModal((m) => ({
      ...m,
      data: {
        ...m.data,
        [field]: m.data[field].map((row, i) => (i === index ? { ...row, [key]: value } : row)),
      },
    }));

  const removeRow = (field, index) =>
    setModal((m) => ({ ...m, data: { ...m.data, [field]: m.data[field].filter((_, i) => i !== index) } }));

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadApi.upload(file, 'products');
      updateField('imageUrl', res.data.url);
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const d = modal.data;
    if (!d.name || !d.price || !d.categoryId) {
      return toast.error('Name, price, and category are required');
    }
    const payload = {
      ...d,
      price: parseFloat(d.price),
      stock: parseInt(d.stock) || 0,
      tags: d.tags ? d.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      nutritionalFacts: (d.nutritionalFacts || []).filter((r) => r.label.trim() || r.value.trim()),
      recipeFor: (d.recipeFor || []).filter((r) => r.ingredient.trim() || r.quantity.trim()),
    };
    try {
      if (modal.mode === 'add') {
        await productApi.create(payload);
        toast.success('Product created');
      } else {
        await productApi.update(d.id, payload);
        toast.success('Product updated');
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error saving product');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await productApi.delete(id);
    toast.success('Product deleted');
    load();
  };

  const filteredSubs = modal?.data?.categoryId
    ? subcategories.filter((s) => s.categoryId === modal.data.categoryId)
    : [];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Products</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <Plus size={18}/> Add Product
        </button>
      </div>

      {/* Search */}
      <div className="search-box" style={{ maxWidth: 360, marginBottom: 20 }}>
        <Search size={18} className="search-icon" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or SKU…"
          className="search-input"
        />
        {search && <button onClick={() => setSearch('')}><X size={16}/></button>}
      </div>

      {loading ? <div className="spinner"/> : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Image</th><th>Name</th><th>Category</th><th>Price</th>
                <th>Stock</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.imageUrl
                      ? <img src={p.imageUrl} alt={p.name} className="table-thumbnail"/>
                      : <Package size={32} className="table-placeholder-icon"/>}
                  </td>
                  <td>
                    <strong>{p.name}</strong>
                    {p.sku && <span className="meta-text"> · {p.sku}</span>}
                  </td>
                  <td>{categories.find((c) => c.id === p.categoryId)?.name || '—'}</td>
                  <td>₹{p.price.toFixed(2)} / {p.unit}</td>
                  <td>{p.stock}</td>
                  <td>
                    <span className={`status-badge ${p.isActive ? 'status-delivered' : 'status-cancelled'}`}>
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button className="icon-btn" onClick={() => openEdit(p)}><Pencil size={16}/></button>
                    <button className="icon-btn danger" onClick={() => handleDelete(p.id, p.name)}>
                      <Trash2 size={16}/>
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="empty-row">No products found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <h2>{modal.mode === 'add' ? 'Add Product' : 'Edit Product'}</h2>

            <div className="form-grid">
              <div className="form-field full">
                <label>Name *</label>
                <input value={modal.data.name} onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Product name" />
              </div>
              <div className="form-field full">
                <label>Description</label>
                <textarea value={modal.data.description} onChange={(e) => updateField('description', e.target.value)}
                  rows={3} placeholder="Product description" />
              </div>
              <div className="form-field">
                <label>Category *</label>
                <select value={modal.data.categoryId}
                  onChange={(e) => { updateField('categoryId', e.target.value); updateField('subcategoryId', ''); }}>
                  <option value="">Select category</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Subcategory</label>
                <select value={modal.data.subcategoryId} onChange={(e) => updateField('subcategoryId', e.target.value)}>
                  <option value="">Select subcategory</option>
                  {filteredSubs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Price (₹) *</label>
                <input type="number" step="0.01" min="0" value={modal.data.price}
                  onChange={(e) => updateField('price', e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-field">
                <label>Stock</label>
                <input type="number" min="0" value={modal.data.stock}
                  onChange={(e) => updateField('stock', e.target.value)} placeholder="0" />
              </div>
              <div className="form-field">
                <label>Unit</label>
                <select value={modal.data.unit} onChange={(e) => updateField('unit', e.target.value)}>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="L">L</option>
                  <option value="ml">ml</option>
                  <option value="piece">piece</option>
                  <option value="pack">pack</option>
                  <option value="box">box</option>
                </select>
              </div>
              <div className="form-field">
                <label>SKU</label>
                <input value={modal.data.sku} onChange={(e) => updateField('sku', e.target.value)}
                  placeholder="SKU-001" />
              </div>
              <div className="form-field full">
                <label>Tags (comma-separated)</label>
                <input value={modal.data.tags} onChange={(e) => updateField('tags', e.target.value)}
                  placeholder="vegan, gluten-free, bestseller" />
              </div>
              <div className="form-field">
                <label>Flavor / Variant</label>
                <input value={modal.data.flavor} onChange={(e) => updateField('flavor', e.target.value)}
                  placeholder="Vanilla" />
              </div>
              <div className="form-field">
                <label>Status</label>
                <select value={modal.data.isActive ? 'true' : 'false'}
                  onChange={(e) => updateField('isActive', e.target.value === 'true')}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div className="form-field full">
                <label>Product Image</label>
                <div className="image-upload-row">
                  {modal.data.imageUrl && (
                    <img src={modal.data.imageUrl} alt="preview" className="upload-preview" />
                  )}
                  <label className="upload-label">
                    <Upload size={18}/> {uploading ? 'Uploading…' : 'Upload Image'}
                    <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                  </label>
                </div>
              </div>

              <div className="form-field full">
                <label>Ingredients</label>
                <textarea value={modal.data.ingredients} onChange={(e) => updateField('ingredients', e.target.value)}
                  rows={3} placeholder="Milk solids, sugar, maltodextrin, stabilizer (INS412, INS401...), emulsifier (INS471), and salt." />
              </div>
              <div className="form-field full">
                <label>Direction for Use</label>
                <textarea value={modal.data.directionsForUse} onChange={(e) => updateField('directionsForUse', e.target.value)}
                  rows={3} placeholder="Mix 1 kg Premix in 6 ltr full-cream milk till it's completely dissolved..." />
              </div>
              <div className="form-field full">
                <label>Best Before / Shelf Life</label>
                <input value={modal.data.shelfLife} onChange={(e) => updateField('shelfLife', e.target.value)}
                  placeholder="Best before 18 months from the date of manufacturing." />
              </div>

              <div className="form-field full">
                <label>Nutritional Facts (per 100 gms approx.)</label>
                <div className="repeatable-rows">
                  {modal.data.nutritionalFacts.map((row, i) => (
                    <div className="repeatable-row" key={i}>
                      <input value={row.label} onChange={(e) => updateRow('nutritionalFacts', i, 'label', e.target.value)}
                        placeholder="Energy" />
                      <input value={row.value} onChange={(e) => updateRow('nutritionalFacts', i, 'value', e.target.value)}
                        placeholder="335 Kcal" />
                      <button type="button" className="icon-btn danger" onClick={() => removeRow('nutritionalFacts', i)}>
                        <Trash2 size={16}/>
                      </button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-ghost btn-sm"
                    onClick={() => addRow('nutritionalFacts', { label: '', value: '' })}>
                    <Plus size={14}/> Add Row
                  </button>
                </div>
              </div>

              <div className="form-field full">
                <label>Recipe For</label>
                <div className="repeatable-rows">
                  {modal.data.recipeFor.map((row, i) => (
                    <div className="repeatable-row" key={i}>
                      <input value={row.ingredient} onChange={(e) => updateRow('recipeFor', i, 'ingredient', e.target.value)}
                        placeholder="Milk" />
                      <input value={row.quantity} onChange={(e) => updateRow('recipeFor', i, 'quantity', e.target.value)}
                        placeholder="6 ltr" />
                      <button type="button" className="icon-btn danger" onClick={() => removeRow('recipeFor', i)}>
                        <Trash2 size={16}/>
                      </button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-ghost btn-sm"
                    onClick={() => addRow('recipeFor', { ingredient: '', quantity: '' })}>
                    <Plus size={14}/> Add Row
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={uploading}>
                {modal.mode === 'add' ? 'Create Product' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
